import type { Action } from "redux";
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  merge,
  mergeMap,
  switchMap,
  takeUntil,
} from "rxjs";
import { concat, from, of } from "rxjs";
import type { Epic } from "redux-observable";
import type { RootState } from "@/store/app/storeTypes";
import type { EpicDependencies } from "@/store/epics/epicDependencies";
import {
  executiveSummarySubscriptionRequested,
  executiveSummarySubscriptionCancelled,
  requestStart,
  requestSuccess,
  REQUEST_KEYS,
} from "@/store/requests/requestsSlice";
import {
  getCatalogSparxsTop3,
  getStatementHistoryStatistics,
  getStatementLeads,
  getStatementSummaries,
} from "@/services/statistics.service";
import { STATEMENT_CHANNEL } from "@/constants/statementChannel";
import type { UserProfile } from "@/hooks/useUserProfile";
import { isUserProfileSessionReady } from "@/lib/session/isUserProfileSessionReady";
import { profileFromState } from "@/store/epics/profileFromState";
import { normalizeArrayResponse } from "@/lib/http/normalizeArrayResponse";
import {
  EXECUTIVE_SUMMARY_SUB_KEYS,
  EXECUTIVE_SUMMARY_TREND_MONTHS,
} from "@/features/executiveSummary/constants";
import {
  toRequestErrorAction,
  watchPeriodScopedPollStreams,
} from "@/store/epics/createPeriodScopedPollingSubscription";

const REQUEST_KEY = REQUEST_KEYS.EXECUTIVE_SUMMARY;

const executiveSummaryEpic: Epic<
  Action,
  Action,
  RootState,
  EpicDependencies
> = (action$, state$, { statisticPollingIntervalMs }) =>
  action$.pipe(
    filter(
      (a): a is ReturnType<typeof executiveSummarySubscriptionRequested> =>
        a.type === executiveSummarySubscriptionRequested.type,
    ),
    mergeMap(() => {
      const cancel$ = action$.pipe(
        filter(
          (a): a is ReturnType<typeof executiveSummarySubscriptionCancelled> =>
            a.type === executiveSummarySubscriptionCancelled.type,
        ),
      );

      const trendOnFi$ = state$.pipe(
        map((state) => profileFromState(state)),
        filter((profile) => isUserProfileSessionReady(profile)),
        distinctUntilChanged(
          (a, b) =>
            a.currentFinancialInstitution === b.currentFinancialInstitution,
        ),
        switchMap((profile) => {
          const userProfile = profile as UserProfile;
          return concat(
            of(
              requestStart({
                requestKey: REQUEST_KEY,
                subKey: EXECUTIVE_SUMMARY_SUB_KEYS.TREND,
              }),
            ),
            from(
              getStatementHistoryStatistics(
                userProfile,
                EXECUTIVE_SUMMARY_TREND_MONTHS,
              ),
            ).pipe(
              map((value) =>
                requestSuccess({
                  requestKey: REQUEST_KEY,
                  subKey: EXECUTIVE_SUMMARY_SUB_KEYS.TREND,
                  data: normalizeArrayResponse(value),
                }),
              ),
              catchError((err: unknown) =>
                of(
                  toRequestErrorAction(
                    REQUEST_KEY,
                    EXECUTIVE_SUMMARY_SUB_KEYS.TREND,
                    err,
                  ),
                ),
              ),
            ),
          );
        }),
        takeUntil(cancel$),
      );

      const periodScoped$ = watchPeriodScopedPollStreams({
        state$,
        cancel$,
        intervalMs: statisticPollingIntervalMs,
        streams: [
          {
            requestKey: REQUEST_KEY,
            subKey: EXECUTIVE_SUMMARY_SUB_KEYS.LEADS,
            fetch: getStatementLeads,
          },
          {
            requestKey: REQUEST_KEY,
            subKey: EXECUTIVE_SUMMARY_SUB_KEYS.SUMMARIES,
            fetch: (profile, period) =>
              getStatementSummaries(profile, STATEMENT_CHANNEL.BOTH, period),
          },
          {
            requestKey: REQUEST_KEY,
            subKey: EXECUTIVE_SUMMARY_SUB_KEYS.TOP3,
            fetch: (profile, period) =>
              getCatalogSparxsTop3(profile, period).pipe(
                map((value) => normalizeArrayResponse(value)),
              ),
          },
        ],
      });

      return merge(trendOnFi$, periodScoped$);
    }),
  );

export default executiveSummaryEpic;
